import { useCurrentAccountId } from '@zapier/identity';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { emitUserInteractionEvent } from '../utils/avroEvents';
import { isEmptyObject } from '../utils/isEmptyObject';

import { useLocalStorage } from '../../utils/useLocalStorage';
import { useMediaProps } from '../../utils/responsive-utils';
import { useQueryTopNav } from './useQueryTopNav';
import type { TopBarLinksType } from '../constants';

import type { ZapierProfile, ZapierAccount } from '../types';
import type { ZapierSession } from '../../types';
import { FALLBACK_TOP_NAV_RESPONSE } from '../constants';
import {
  UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
  UNIVERSAL_SIDEBAR_WIDTH,
} from '../constants';
import { useQuerySession } from './useQuerySession';

type UniversalNavContext = {
  apiHostname: string;
  accountData?: ZapierAccount;
  accountsListData?: Array<ZapierProfile>;
  bffHostname: string;
  canForceSidebarOpen: boolean;
  canTopbarFitBackToAppButton: boolean;
  canTopbarFitLinks: boolean;
  currentAccountId?: number;
  currentUrl: string;
  isCreateMenuOpen: boolean;
  isLoadingLoggedIn: boolean;
  isLoggedIn: boolean;
  isLoadingTopNav: boolean;
  isOnAppPage: boolean;
  isInAppExperience: boolean;
  isSearchFullWidth: boolean;
  isSidebarControlledByTopbar: boolean;
  isSidebarForcedOpen: boolean;
  isSidebarOpenedByTopbar: boolean;
  isSidebarRendered: boolean;
  mode?: 'in-app' | 'navigation';
  topNavData: TopBarLinksType;
  onToggleSidebarVisibility?: () => void;
  sessionData?: ZapierSession;
  setIsCreateMenuOpen: (state: boolean) => void;
  setIsSidebarForcedOpen: (state: boolean) => void;
  setIsSidebarOpenedByTopbar: (state: boolean) => void;
  setIsSidebarRendered: (state: boolean) => void;
  shouldRenderCallToAction: boolean;
  shouldRenderUpgradeCTA: boolean;
  shouldRenderSearch: boolean;
  shouldRenderSidebar: boolean;
  shouldRenderAccessibleSidebar: boolean;
  shouldRenderTopbarLinks: boolean;
  shouldRenderTOS?: boolean;
  shouldRenderUniversalNav: boolean;
  sidebarWidth: number;
  topbarSize: 'small' | 'medium';
  urlPathPrefix?: string;
  getFullyQualifiedURL: (path?: string | null) => string | undefined;
};

const defaultContext: UniversalNavContext = {
  apiHostname: '',
  bffHostname: '',
  canForceSidebarOpen: false,
  canTopbarFitBackToAppButton: false,
  canTopbarFitLinks: false,
  currentAccountId: undefined,
  currentUrl: '',
  isCreateMenuOpen: false,
  isLoadingLoggedIn: true,
  isLoadingTopNav: true,
  isLoggedIn: false,
  isOnAppPage: false,
  isInAppExperience: false,
  isSearchFullWidth: true,
  isSidebarControlledByTopbar: false,
  isSidebarForcedOpen: false,
  isSidebarOpenedByTopbar: false,
  isSidebarRendered: false,
  onToggleSidebarVisibility: () => {},
  setIsCreateMenuOpen: () => {},
  setIsSidebarForcedOpen: () => {},
  setIsSidebarOpenedByTopbar: () => {},
  setIsSidebarRendered: () => {},
  shouldRenderCallToAction: false,
  shouldRenderUpgradeCTA: false,
  shouldRenderSearch: true,
  shouldRenderSidebar: false,
  shouldRenderAccessibleSidebar: false,
  shouldRenderTopbarLinks: false,
  shouldRenderTOS: false,
  shouldRenderUniversalNav: true,
  sidebarWidth: 0,
  topbarSize: 'medium',

  topNavData: {},
  // @ts-ignore. This is a default value.
  getFullyQualifiedURL: (path?: string | null) => path || undefined,
};

export const UniversalNavContext = createContext(defaultContext);

export const useUniversalNavContext = () => useContext(UniversalNavContext);

type Props = {
  /**
   * Optionally specify the rendering mode for UniversalLayout:
   *   `in-app`: The logged-in experience including the sidebar menu.
   *   `navigation`: No sidebar, but with navigation links in the topbar.
   *
   * If omitted, the mode will be inferred from the `currentUrl` prop.
   */
  mode?: 'in-app' | 'navigation';
  /*
   * Account data for the current user.
   */
  accountData?: ZapierAccount;
  /**
   * tbd
   */
  accountsListData?: Array<ZapierProfile>;
  /**
   * What to render within the provider.
   */
  children: React.ReactNode;
  /**
   * `id` of the current account. Will be falsey if user isn't logged in.
   */
  currentAccountId?: number;
  /**
   * Current path in the browser's address bar.
   */
  currentUrl: string;
  /**
   * Whether or not to show a `tempory` TOS in the logged in sidebar
   * Temporary feature for Nov/Dec 2022
   */
  shouldRenderTOS?: boolean;
  /**
   * Optional hostname prop that will render as absolute path instead of a relative path.
   */
  urlPathPrefix?: string;
  /*
   * Session data for the current user.
   */
  sessionData?: ZapierSession;
  /**
   * Force API requests to be sent to this hostname, instead of the current domain.
   * Most likely needed for applications running on a subdomain.
   */
  apiHostname?: string;
  /**
   * Force BFF requests to be sent to this hostname, instead of relying on defaults.
   * This is useful when developing against a local or preview BFF server.
   * @default https://zapier-staging.com for staging and https://zapier.com for production
   */
  bffHostname?: string;
};

const isCurrentUrlAnAuthPage = (currentUrl: string) => {
  return (
    currentUrl.includes('/login') ||
    currentUrl.includes('/sign-up') ||
    currentUrl.includes('/oauth/authorize')
  );
};

const isCurrentUrlAnAppPage = (currentUrl: string, mode?: string) => {
  if (mode) {
    return mode === 'in-app' ? true : false;
  }
  return currentUrl.includes('/app/');
};

const isCurrentUrlAnOnboardingPage = (currentUrl: string) => {
  return currentUrl.includes('/app/onboarding');
};

const shouldRenderSidebarForRoute = ({
  currentUrl,
  mode,
}: {
  currentUrl: string;
  mode?: Props['mode'];
}) => {
  return (
    isCurrentUrlAnAppPage(currentUrl, mode) &&
    !isCurrentUrlAnAuthPage(currentUrl) &&
    !isCurrentUrlAnOnboardingPage(currentUrl)
  );
};

export const shouldRenderTopbarLinksForRoute = ({
  currentUrl,
  mode,
}: {
  currentUrl: string;
  mode?: Props['mode'];
}) => {
  return (
    !isCurrentUrlAnAppPage(currentUrl, mode) &&
    !isCurrentUrlAnAuthPage(currentUrl)
  );
};

const getShouldRenderSidebar = ({
  isLoggedIn,
  canTopbarFitLinks,
  currentUrl,
  isSidebarControlledByTopbar,
  isSidebarOpenedByTopbar,
  isCreateMenuOpen,
  mode,
}: {
  isLoggedIn: boolean;
  canTopbarFitLinks: boolean;
  currentUrl: string;
  isSidebarControlledByTopbar: boolean;
  isSidebarOpenedByTopbar: boolean;
  isCreateMenuOpen: boolean;
  mode?: Props['mode'];
}) => {
  if (isCreateMenuOpen) return true;

  if (isSidebarControlledByTopbar) {
    return isSidebarOpenedByTopbar;
  }

  if (mode && mode === 'navigation') {
    return canTopbarFitLinks === false ? true : false;
  }

  if (mode && mode === 'in-app' && isLoggedIn) {
    return true;
  }

  if (isLoggedIn && shouldRenderSidebarForRoute({ currentUrl, mode })) {
    return true;
  }
  if (
    !canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({ currentUrl, mode })
  ) {
    return true;
  }
  return false;
};

const getShouldRenderAccessibleSidebar = ({
  isLoggedIn,
  canTopbarFitLinks,
  currentUrl,
  isSidebarControlledByTopbar,
  isSidebarOpenedByTopbar,
  mode,
}: {
  isLoggedIn: boolean;
  canTopbarFitLinks: boolean;
  currentUrl: string;
  isSidebarControlledByTopbar: boolean;
  isSidebarOpenedByTopbar: boolean;
  mode?: Props['mode'];
}) => {
  if (isSidebarControlledByTopbar) {
    return !isSidebarOpenedByTopbar;
  }
  if (mode && mode === 'navigation') {
    return canTopbarFitLinks === false ? true : false;
  }

  if (mode && mode === 'in-app' && isLoggedIn && canTopbarFitLinks) {
    return false;
  }

  if (
    isLoggedIn &&
    shouldRenderSidebarForRoute({ currentUrl, mode }) &&
    !canTopbarFitLinks
  ) {
    return true;
  }
  if (
    !canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({ currentUrl, mode })
  ) {
    return true;
  }
  return false;
};

const getShouldRenderSearch = ({ currentUrl }: { currentUrl: string }) => {
  if (
    isCurrentUrlAnAuthPage(currentUrl) ||
    isCurrentUrlAnOnboardingPage(currentUrl)
  ) {
    return false;
  }
  return true;
};

const getShouldRenderTopbarToggle = ({
  isSidebarControlledByTopbar,
  isLoggedIn,
  currentUrl,
  mode,
}: {
  isSidebarControlledByTopbar: boolean;
  isLoggedIn: boolean;
  currentUrl: string;
  mode?: Props['mode'];
}) => {
  // If the sidebar isn't controlled by the topbar then
  // the toggle should never render, so bail immediately.
  if (!isSidebarControlledByTopbar) {
    return false;
  }
  if (isLoggedIn) {
    return true;
  }
  return shouldRenderTopbarLinksForRoute({
    currentUrl,
    mode,
  });
};

const getSidebarWidth = ({
  canForceSidebarOpen,
  isSidebarForcedOpen,
  isSidebarRendered,
  isSidebarControlledByTopbar,
}: {
  canForceSidebarOpen: boolean;
  isSidebarForcedOpen: boolean;
  isSidebarRendered: boolean;
  isSidebarControlledByTopbar: boolean;
}) => {
  if (!isSidebarRendered || isSidebarControlledByTopbar) {
    return 0;
  }
  if (canForceSidebarOpen && isSidebarForcedOpen) {
    return UNIVERSAL_SIDEBAR_WIDTH;
  }
  return UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH;
};

export const UniversalNavProvider = (props: Props) => {
  const { data: sessionData } = useQuerySession(
    props.sessionData,
    props.apiHostname
  );
  const currentAccountId =
    useCurrentAccountId() || sessionData?.currentAccountId;

  const {
    accountData: accountUsageData,
    accountsListData,
    currentUrl,
    mode,
    sessionData: sessionDataFromProps,
    shouldRenderTOS,
    urlPathPrefix,
  } = props;

  const isLoggedIn = sessionData ? sessionData.isLoggedIn : false;

  const isInAppExperience =
    isCurrentUrlAnAppPage(currentUrl, mode) &&
    false === isCurrentUrlAnOnboardingPage(currentUrl) &&
    false === isCurrentUrlAnAuthPage(currentUrl);

  const {
    isLoading: isLoadingTopNav,
    // topNav fallback data is set here instead of in the useQueryTopNav hook because setting placeholderData in a react-query query will prevent isLoading from ever being true.
    // isLoadingTopNav must initially be true in order for `UniversalNav` to render the `TopBarLoadingState` component, which is necessary for SEO.
    // isLoadingTopNav should be false when re-fetching (i.e. when the user leaves the browser and returns), or the component will flash as it switches between `InAppTopbar`/`OutOfAppTopbar` -> `TopBarLoadingState` -> `InAppTopbar`/`OutOfAppTopbar`.
    data: topNav = FALLBACK_TOP_NAV_RESPONSE,
  } = useQueryTopNav({
    apiHostname: props.apiHostname || '',
    bffHostname: props.bffHostname || '',
    currentAccountId,
    customUserId: sessionData?.customUserId,
    isInAppExperience,
  });

  const isOnAppPage = isCurrentUrlAnAppPage(currentUrl, mode);

  const {
    isSearchFullWidth,
    isSidebarControlledByTopbar,
    canForceSidebarOpen,
    canTopbarFitBackToAppButton,
    canTopbarFitLinks,
    topbarSize,
  } = useMediaProps({
    '(min-width: 0px)': {
      isSidebarControlledByTopbar: true,
      isSearchFullWidth: true,
      canForceSidebarOpen: false,
      canTopbarFitBackToAppButton: false,
      canTopbarFitLinks: false,
      topbarSize: 'small',
    },
    '(min-width: 450px)': {
      canTopbarFitBackToAppButton:
        isEmptyObject(topNav.callToAction) && isEmptyObject(topNav.upgradeCTA),
      topbarSize: 'small',
    },
    '(min-width: 800px)': {
      ...(isEmptyObject(topNav.upgradeCTA)
        ? {
            canTopbarFitBackToAppButton: true,
            topbarSize: 'medium',
          }
        : { canTopbarFitBackToAppButton: false, topbarSize: 'small' }),
    },
    '(min-width: 1040px)': {
      topbarSize: 'medium',
      canTopbarFitBackToAppButton: true,
      isSidebarControlledByTopbar: false,
      isSearchFullWidth: false,
      canTopbarFitLinks: true,
    },
    '(min-width: 1280px)': {
      canForceSidebarOpen: shouldRenderSidebarForRoute({ currentUrl, mode }),
    },
  });

  const shouldRenderTopbarLinks =
    canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({
      currentUrl,
      mode,
    });

  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);

  const [isSidebarOpenedByTopbar, setIsSidebarOpenedByTopbar] = useState(false);
  const [isSidebarForcedOpen, setIsSidebarForcedOpen] = useLocalStorage(
    'UniversalNav:isSidebarForcedOpen'
  );
  // Default to opening sidebar if the user hasn't chosen yet.
  useEffect(() => {
    if (isSidebarForcedOpen === null || isSidebarForcedOpen === undefined) {
      setIsSidebarForcedOpen(true);
    }
  }, [isSidebarForcedOpen, setIsSidebarForcedOpen]);
  const [isSidebarRendered, setIsSidebarRendered] = useState(false);
  const shouldRenderSidebar = getShouldRenderSidebar({
    isLoggedIn,
    canTopbarFitLinks,
    currentUrl,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    isCreateMenuOpen,
    mode,
  });

  const shouldRenderAccessibleSidebar = getShouldRenderAccessibleSidebar({
    isLoggedIn,
    canTopbarFitLinks,
    currentUrl,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    mode,
  });
  const shouldRenderSearch = getShouldRenderSearch({
    currentUrl,
  });
  const shouldRenderTopbarToggle = getShouldRenderTopbarToggle({
    isSidebarControlledByTopbar,
    isLoggedIn,
    currentUrl,
  });
  const onToggleSidebarVisibility = shouldRenderTopbarToggle
    ? () => {
        emitUserInteractionEvent({
          event_action: 'click',
          option_selected: `UniversalTopbar SidebarVisibilityToggle`,
        });

        setIsSidebarOpenedByTopbar(!isSidebarOpenedByTopbar);
      }
    : undefined;
  const sidebarWidth = getSidebarWidth({
    canForceSidebarOpen,
    isSidebarForcedOpen,
    isSidebarRendered,
    isSidebarControlledByTopbar,
  });

  const memoizedFullyQualifiedUrl = useCallback(
    (link?: string | null): string | undefined => {
      if (link && urlPathPrefix && link.startsWith('/'))
        return urlPathPrefix + link;

      return link || undefined;
    },
    [urlPathPrefix]
  );

  const value = {
    accountUsageData,
    accountsListData,
    currentAccountId,
    isLoadingTopNav,
    canTopbarFitBackToAppButton,
    canTopbarFitLinks,
    canForceSidebarOpen,
    currentUrl,
    isCreateMenuOpen,
    isLoadingLoggedIn: !sessionData,
    isLoggedIn,
    isOnAppPage,
    isInAppExperience,
    isSearchFullWidth,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    isSidebarForcedOpen:
      canForceSidebarOpen && isSidebarRendered ? isSidebarForcedOpen : false,
    isSidebarRendered,
    mode,
    onToggleSidebarVisibility,
    sessionData: sessionDataFromProps,
    setIsCreateMenuOpen,
    setIsSidebarOpenedByTopbar,
    setIsSidebarForcedOpen,
    setIsSidebarRendered,
    shouldRenderCallToAction: !isCurrentUrlAnOnboardingPage(currentUrl),
    shouldRenderUpgradeCTA: !isCurrentUrlAnOnboardingPage(currentUrl),
    shouldRenderSearch,
    shouldRenderSidebar,
    shouldRenderAccessibleSidebar,
    shouldRenderTopbarLinks,
    shouldRenderTOS,
    shouldRenderUniversalNav: true,
    sidebarWidth,
    topbarSize,
    topNavData: topNav,
    urlPathPrefix,
    getFullyQualifiedURL: memoizedFullyQualifiedUrl,
    apiHostname: props.apiHostname || '',
    bffHostname: props.bffHostname || '',
  };
  return (
    <UniversalNavContext.Provider value={value}>
      {props.children}
    </UniversalNavContext.Provider>
  );
};
